import { memo, useEffect, useRef, useState } from 'react';
import { Handle, Position, NodeToolbar } from '@xyflow/react';
import { useCompanyBranch } from '../../hooks/useCompanyBranch';
import { formatCurrency, formatDateTime } from '@/common/utils/formatters';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import { BriefcaseBusinessIcon, InfoIcon, PlusIcon } from 'lucide-react';
import ImageWithDisplayName from '@/common/components/ImageWithDisplayName';
import { useCompanyBranchFinancialSummary } from '../../hooks/useCompanyBranchFinancialSummary';
import FinancialSummaryLoadingState from './components/FinancialSummaryLoadingState';
import FinancialSummary from './components/FinancialSummary';
import BranchDataLoadingState from './components/BranchDataLoadingState';
import Button from '@/common/components/Button';
import QueryString from 'qs';
import { CurrentContract } from '@/features/companies/types/companies.type';
import { renderHighlightedText } from './services/renderHighlightedText';

interface CustomNodeProps {
  data: {
    companyId: string;
    display_name: string;
    id: string;
    parent_id?: string;
    parentRef: React.RefObject<HTMLDivElement>;
    companyKind?: string;
    status?: string
    consumptionControlledBy?: CurrentContract['consumption_controlled_by']
    searchTerm: string
    isMatch: boolean
    isActiveMatch: boolean
  }
}

function CustomNode({ data }: CustomNodeProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [enableRequest, setEnableRequest] = useState(false);
  const [tooltipPositionSetted, setTooltipPositionSetted] = useState(false);
  const [createBranchUrl, setCreateBranchUrl] = useState('');
  const tooltipHeightRef = useRef(0);
  const tooltipSectionRef = useRef(null)

  const requested = useRef(false);

  const { data: branchData, isPending: isBranchDataPending } = useCompanyBranch(data.id || '', enableRequest);
  const {
    data: financialSummary,
    isPending: isFinancialSummaryPending,
  } = useCompanyBranchFinancialSummary(data.id || '', enableRequest);

  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
  const [tooltipStyle, setTooltipStyle] = useState<{ top?: string; bottom?: string; left: string }>({ top: '0px', bottom: '0px', left: '0px' });

  const nodeRef = useRef<HTMLDivElement>(null);
  const firstColumnRef = useRef<HTMLDivElement>(null);

  const [secondColumnHeight, setSecondColumnHeight] = useState(0);

  const tooltipWidth = 416;

  useEffect(() => {
    if (tooltipOpen && nodeRef.current && data.parentRef.current) {
      const nodeRect = nodeRef.current.getBoundingClientRect();
      const parentRect = data.parentRef.current.getBoundingClientRect();
      const el = tooltipSectionRef.current;
      if(!el) return

      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if(newHeight > 220) {
            tooltipHeightRef.current = newHeight
          }
        }
      });

      observer.observe(el);

      const spaceTop = nodeRect.top - parentRect.top;
      const spaceBottom = parentRect.bottom - nodeRect.bottom;
      const tooltipHeight = tooltipHeightRef.current || 300;

      if (spaceTop > spaceBottom || spaceTop > 300) {
        setTooltipPosition('top');
      } else {
        setTooltipPosition('bottom');
      }

      const spaceLeft = nodeRect.left - parentRect.left;
      const spaceRight = parentRect.right - nodeRect.right;

      let adjustedLeft = 0;

      if (spaceRight < tooltipWidth / 2) {
        adjustedLeft = -(tooltipWidth / 2 - spaceRight - nodeRect.width / 2);
      } else if (spaceLeft < tooltipWidth / 2) {
        adjustedLeft = Math.max(adjustedLeft, tooltipWidth / 2 - spaceLeft - nodeRect.width / 2);
      }

      if(spaceTop < tooltipHeight && spaceBottom < tooltipHeight) {
        const maxSpace = Math.max(spaceBottom, spaceTop)

        if(spaceTop > spaceBottom) {
          setTooltipStyle({ top: `${Math.round(tooltipHeight - maxSpace) + 10}px`, left: `${adjustedLeft}px` });
        } else {
          setTooltipStyle({ top: `-${Math.round(tooltipHeight - maxSpace) + 10}px`, left: `${adjustedLeft}px` });
        }

        return setTooltipPositionSetted(true);
      }

      setTooltipStyle({ left: `${adjustedLeft}px` });
      setTooltipPositionSetted(true);

      return () => observer.disconnect();
    }
  }, [tooltipOpen]);

  useEffect(() => {
    if (firstColumnRef.current && tooltipOpen && secondColumnHeight === 0) {
      setSecondColumnHeight(firstColumnRef.current.getBoundingClientRect().height);
    }
  }, [tooltipOpen, isBranchDataPending, firstColumnRef.current]);

  useEffect(() => {
    if (isBranchDataPending) {
      return;
    }

    const branchOption = {
      parent: {
        label: data.display_name,
        value: data.id,
      },
    };

    const url = `/companies/${data.companyId}/branches/create?${QueryString.stringify(branchOption)}`;

    setCreateBranchUrl(url);
  }, [isBranchDataPending])

  return (
    <div
      ref={nodeRef}
      className="p-5 pl-0"
      onMouseOver={() => {
        if (!requested.current) {
          setEnableRequest(true);
          requested.current = true;
        }

        setTooltipOpen(true)
      }}
      onMouseLeave={() => setTooltipOpen(false)}
    >
      <div
        className={twMerge(
          'relative px-4 py-2 rounded-md cursor-pointer',
          tooltipOpen && 'bg-white-200',
          data.status === 'disabled' && 'border border-danger'
        )}
      >
        <NodeToolbar
          isVisible={tooltipOpen}
          position={tooltipPosition as Position}
          style={tooltipStyle}
        >
          <div className={twMerge('-my-3 nowheel', !tooltipPositionSetted && 'hidden')}>
            <div
              className={`w-[${tooltipWidth}px] flex bg-white-200 backdrop-blur-lg z-50 rounded-md cursor-default overflow-hidden`}
              ref={tooltipSectionRef}
            >
              {isBranchDataPending && <BranchDataLoadingState />}

              {!isBranchDataPending && (
                <div className="w-[216px] p-4 flex flex-col gap-2" ref={firstColumnRef}>
                  {data.status === 'disabled' && (
                    <div className='flex items-center gap-2 p-2 border border-danger rounded-sm'>
                      <InfoIcon strokeWidth={1} className="text-danger" />
                      <p className='text-xs'>Filial bloqueada</p>
                    </div>
                  )}
                  <ImageWithDisplayName
                    placeholderIcon={BriefcaseBusinessIcon}
                    imageUrl={branchData?.general_information?.company_logo?.url}
                    displayName={branchData?.display_name}
                    displayNameClassName="text-sm font-bold"
                  />

                  {data.companyKind === 'public_company' ? (
                    <div>
                      <p className='text-xs font-light italic'>
                        Cota de empenho: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.granted_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic'>
                        Total empenhado: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.government_allocation_requested_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic'>
                        Total distribuído: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.total_government_allocation_distributed_to_branch) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-yellow-400'>
                        Total consumido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.consumed_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-valid'>
                        Total disponível: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.remaining_credit) || 0)}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className='text-xs font-light italic'>
                        Valor garantido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.granted_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-yellow-400'>
                        Total consumido: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.consumed_credit) || 0)}
                        </span>
                      </p>

                      <p className='text-xs font-light italic text-valid'>
                        Total disponível: <span className='font-bold not-italic'>
                          {formatCurrency(Number(branchData?.remaining_credit) || 0)}
                        </span>
                      </p>
                    </div>
                  )}

                  <div className='flex items-baseline gap-1'>
                    <p className='italic font-light text-sm'>
                      Equipamentos:
                    </p>

                    <Link to={`/companies/${data.companyId}/equipments?branch%5Blabel%5D=${branchData?.display_name}&branch%5Bvalue%5D=${data.id}&status%5Bvalue%5D=`}>
                      <p className='text-xs underline'>
                        {branchData?.equipments_count}
                      </p>
                    </Link>
                  </div>

                  <div className='flex items-baseline gap-1'>
                    <p className='italic font-light text-sm'>
                      Usuários:
                    </p>

                    <Link to={`/companies/${data.companyId}/users?branch%5Blabel%5D=${branchData?.display_name}&branch%5Bvalue%5D=${data.id}`}>
                      <p className='text-xs underline'>
                        {branchData?.client_users_count}
                      </p>
                    </Link>
                  </div>

                  <p className='text-[9px] italic font-light'>
                    {`Cadastrado em ${formatDateTime(new Date(branchData?.created_at || 0))}`}
                  </p>
                </div>
              )}

              {secondColumnHeight > 0 && (
                <div className='w-[200px] p-2 bg-black-900'>
                  <div
                    className='flex flex-col p-2 gap-2 overflow-auto thin-scroll'
                    style={{ height: secondColumnHeight }}
                  >
                    {isFinancialSummaryPending && <FinancialSummaryLoadingState />}

                    {!isFinancialSummaryPending && (
                      <FinancialSummary
                        branchId={data.id}
                        financialSummary={financialSummary}
                        companyKind={data.companyKind}
                        consumptionControlledBy={data.consumptionControlledBy}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </NodeToolbar>

        <div className="relative flex items-center gap-x-2">
          <Link to={`/companies/${data.companyId}/branches/${data.id}`} className="flex items-center p-2">
            <ImageWithPlaceholder
              className={twMerge('size-6', !data.parent_id && 'size-8')}
              iconClassName={twMerge('size-4', !data.parent_id && 'size-6')}
              placeholderIcon={BriefcaseBusinessIcon}
              url={branchData?.general_information?.company_logo?.url}
              alt={branchData?.general_information?.fantasy_name}
            />

            <div className="ml-2 flex-1 max-w-44">
              <div className={twMerge("text-lg font-bold break-words whitespace-normal", !data.parent_id && 'text-3xl')}>
                {data.isMatch
                  ? renderHighlightedText(
                      data.display_name,
                      data.searchTerm,
                      data.isActiveMatch
                    )
                  : data.display_name}
              </div>
            </div>
          </Link>

          <Link
            to={createBranchUrl}
            className={twMerge('hidden absolute -bottom-6 -right-8', tooltipOpen && 'flex')}
          >
            <Button
              variant="link-white"
              className="bg-success border-2 border-white-800 p-0.5 rounded-full"
              operationKlassToCheckPermission="Admin::Client::CompanyBranches::Create"
            >
              <PlusIcon className="size-6" strokeWidth={2} />
            </Button>
          </Link>
        </div>

        <Handle
          type="target"
          position={Position.Top}
          className='invisible'
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className='invisible'
        />
      </div>
    </div>
  );
}

export default memo(CustomNode);
